<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Layout</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <v-checkbox v-model="fields.layout.centerY" label="Center Y" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <label>Gap Item (px)</label>
                <v-text-field v-model.number="fields.layout.gapItem" type="number" hide-details class="mt-0" />
              </v-col>
              <v-col cols="12" md="4">
                <label>Content Position</label>
                <v-select
                  v-model.number="fields.layout.contentOrder"
                  :items="[
                    { text: 'Left', value: 1 },
                    { text: 'Right', value: 2 },
                  ]"
                  hide-details
                  class="mt-0"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Title & Description</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.titleContent" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">CTA Button</label>
          <div class="group-item">
            <ButtonPrimary :button="fields.button" :show-policy="false" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Signature Image</label>
          <div class="group-item">
            <v-checkbox v-model="fields.signature.show" label="Show signature" hide-details class="mt-0 mb-2" />
            <UploadImage
              v-if="fields.signature.show"
              :image="{ imageSrc: fields.signature.image }"
              :height="`120`"
              :typeUpload="'image'"
              @callback="obj => (fields.signature.image = obj.imageSrc)"
              @callbackDelete="fields.signature.image = ''"
            />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Images (max {{ MAX_IMAGE_ITEMS }})</label>
            <v-btn small color="primary" :disabled="fields.images.length >= MAX_IMAGE_ITEMS" @click="handleAddImage">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Image</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-row>
              <v-col cols="12" md="6">
                <label>Border Radius </label>
                <v-text-field
                  v-model.number="fields.layout.borderImage"
                  type="number"
                  min="0"
                  hide-details
                  class="mt-0"
                  suffix="px"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  :value="fields.layout.borderColorImage"
                  name="border-color-image"
                  label="Border Color"
                  default-color="#006196"
                  @input="value => (fields.layout.borderColorImage = value)"
                />
              </v-col>
            </v-row>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.images" :key="`image-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Image {{ index + 1 }}</span>
                    <v-btn
                      icon
                      :disabled="fields.images.length <= MIN_IMAGE_ITEMS"
                      @click.stop="handleRemoveImage(index)"
                    >
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <UploadImage
                    :image="{ imageSrc: item.image }"
                    :height="`260`"
                    :typeUpload="'image'"
                    @callback="obj => (item.image = obj.imageSrc)"
                    @callbackDelete="item.image = ''"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import ButtonPrimary from '../common/ui-modules/button-primary/editor.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const MAX_IMAGE_ITEMS = 3;
const MIN_IMAGE_ITEMS = 1;

const buildButtonDefault = () => ({
  align: 'flex-start',
  backgroundColor: '#606F99',
  color: '#FFFFFF',
  radius: 4,
  iconPrefix: { show: false, src: '' },
  iconSuffix: {
    show: true,
    src: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/03/image-6a1f8e0dd4865b45728c40d9.png',
  },
  text: 'Shop Now',
  link: '#',
  show: true,
  subBtn: { show: false, text: '30-Day Money-Back Guararantee' },
  policy: { show: false, colPerView: 2, alignContent: 'center', items: [] },
});

const buildImageItemDefault = () => ({
  image: '',
});

const getDefaultFields = () => ({
  title: 'Image With Text Conven Feet 01 Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  layout: {
    centerY: true,
    gapItem: 112,
    contentOrder: 1,
    borderImage: 10,
    borderColorImage: '#006196',
  },
  titleContent:
    "<h2>OUR STORY</h2><p>As a physical therapist of 14 years, I've seen too many patients walk into my clinic defeated by chronic pain...</p><p>After treating over 1,200 patients, I noticed one devastating pattern: they were spending hundreds of dollars on 'high-end' sneakers and rigid orthotics, only for pain.</p>",
  button: buildButtonDefault(),
  signature: {
    show: true,
    image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/22/image-6a38f4dc1414e7524f4fcd9a.png',
  },
  images: [
    {
      image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/22/image-6a38f349b0c7b0911a3ce8ed.png',
    },
    {
      image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/22/image-6a38f36464265353a3df3e77.png',
    },
    {
      image: 'https://cdn.skyfastimg.com/6a0ee559dc2c2e000934dd37/2026/06/22/image-6a38f381e4eab8147a94683b.png',
    },
  ],
});

export default {
  name: 'ImageWithTextConvenFeet01SectionEditor',
  components: { UploadImage, ButtonPrimary, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MAX_IMAGE_ITEMS,
      MIN_IMAGE_ITEMS,
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      layout: { ...def.layout, ...(extract.layout || {}) },
      button: { ...def.button, ...(extract.button || {}) },
      signature: { ...def.signature, ...(extract.signature || {}) },
      images:
        Array.isArray(extract.images) && extract.images.length ? extract.images.slice(0, MAX_IMAGE_ITEMS) : def.images,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        images: (this.fields.images || []).slice(0, MAX_IMAGE_ITEMS),
      };
      const updateHtml = updateHtmlFromData.sectionImageWithTextConvenFeet01(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddImage() {
      if (this.fields.images.length >= MAX_IMAGE_ITEMS) return;
      this.fields.images.push(cloneDeep(buildImageItemDefault()));
    },
    handleRemoveImage(index) {
      if (this.fields.images.length <= MIN_IMAGE_ITEMS) return;
      this.fields.images.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
